import React, { useEffect, useState, type FC } from "react";
import UtilityPanel from "../layout/UtilityPanel";
import CorePanelFooter from "../layout/CorePanelFooter";
import CoreLayout from "../layout/CoreLayout";
import { useModal } from "../../hooks/useModal";
import Button from "../form/Button";
import { useAppStore } from "../../store/appStore";
import type { UserAccount } from "../../api/dev-data/getSessionStatus";
import { submitAuthorizedAccounts } from "../../api/api";
import { withErrorBoundary } from "../components/withErrorBoundary";

interface AccountItemProps {
	account: UserAccount;
	isSelected: boolean;
	onSelectionChange: (accountNumber: string, isSelected: boolean) => void;
}

const SelectableAccountItem: FC<AccountItemProps> = ({ account, isSelected, onSelectionChange }) => {
	return (
        <div className="mb-4">
            <label className="cursor-pointer">
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) =>
                        onSelectionChange(
                            account.utilityAccountNumber,
                            e.target.checked
                        )
                    }
                    className="mr-2"
                />
                <span className="font-semibold">
                    {account.accountNickname != null &&
                    account.accountNickname !== ""
                        ? account.accountNickname
                        : "Account with Unknown Nickname"}
                </span>
                <div className="text-sm text-slate-600 ml-6">
                    {account.utilityAccountNumberWithCheckDigit}
                </div>
            </label>
        </div>
    );
};

interface Props {
	getSessionId: () => string | null;
	accounts: UserAccount[];
	cancelSession: () => void;
}

const AuthzAccounts: FC<Props> = (props) => {
	const { setSessionStatus, setForceCheckSessionStatus } = useAppStore();
	const { showModal } = useModal();
	const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());

	// Initialize all accounts as selected by default
	useEffect(() => {
		const allAccountNumbers = new Set(props.accounts.map(account => account.utilityAccountNumber));
		setSelectedAccounts(allAccountNumbers);
	}, [props.accounts]);

	const handleSelectionChange = (accountNumber: string, isSelected: boolean) => {
		setSelectedAccounts(prev => {
			const newSet = new Set(prev);
			if (isSelected) {
				newSet.add(accountNumber);
			} else {
				newSet.delete(accountNumber);
			}
			return newSet;
		});
	};

	function onNext() {
		const sessionId = props.getSessionId();
		if (sessionId == null) {
			return;
		}

		submitAuthorizedAccounts(sessionId, props.accounts.filter(account => selectedAccounts.has(account.utilityAccountNumber)));
		setForceCheckSessionStatus(true);
		setSessionStatus("AUTHZ_SUBMITTING");
	}

	return (
		<CoreLayout>
			<UtilityPanel title="Which accounts do you want to authorize?">
				{props.accounts.map((account) => (
					<SelectableAccountItem 
						key={account.utilityAccountNumber} 
						account={account} 
						isSelected={selectedAccounts.has(account.utilityAccountNumber)}
						onSelectionChange={handleSelectionChange}
					/>
				))}
			</UtilityPanel>
			<CorePanelFooter>
				<Button
					onClick={onNext}
					type="primary"
				>
					Continue
				</Button>
				<Button
					onClick={() =>
						showModal(
							"cancel",
							"Are you sure you want to cancel?",
							"Cancelling will end the authorization process.",
							props.cancelSession,
						)
					}
					type="primary"
					subType="outline"
				>
					Cancel
				</Button>
			</CorePanelFooter>
		</CoreLayout>
	);
};

export default withErrorBoundary(AuthzAccounts);
